/**
 * Which price goes in which row of the standard orderbook — and the rule that
 * keeps the row under the pointer still.
 *
 * `<OrderbookLadder>` shows ten asks, the spread and ten bids, and slides levels
 * through those rows as the book moves: a better ask appearing pushes every ask
 * row down by one. That is fine for a panel you read. It is not fine for one you
 * click, and with click to trade the standard book is one: in the last seconds
 * of a market the book changes many times a second, and a user aiming at 47¢
 * whose rows shift as the pointer goes down trades at 46¢.
 *
 * So while the pointer is over the book the layout is **anchored**: the entry
 * under the pointer stays in its row and the rest of the book is laid out
 * around it.
 *
 * - A new price **between the anchor and the spread** is inserted there, and the
 *   spread and the far side move a row away. Nothing is hidden — the user sees
 *   the new price as it arrives.
 * - A new price **beyond the anchor** goes on the far side, as it always did.
 * - An anchored price that **empties** stays, as a *ghost* row with no size, for
 *   as long as the pointer is on it. A click on it is still a limit order at
 *   the price the user aimed at, which never trades worse than that price.
 *
 * The panel's height is fixed, so the far end of the book can lose a row while
 * anchored; it comes back when the pointer leaves and the layout returns to
 * normal. With no anchor this is exactly the ladder's original layout.
 *
 * No React, no DOM, no clock (invariant 10). Prices are integer cents.
 */

export type SlotKind = 'ask' | 'bid' | 'spread' | 'empty'

export interface Slot {
  kind: SlotKind
  /** The level's price; `null` for the spread and for an empty slot. */
  cents: number | null
  /** An anchored price that has left the book, kept so it does not move. */
  ghost: boolean
}

/** The entry held in place, and the row it is held in. */
export interface Anchor {
  kind: 'ask' | 'bid' | 'spread'
  /** `null` exactly when `kind` is `spread`. */
  cents: number | null
  slot: number
}

/** Rows a `layoutSlots` call returns for `rowsPerSide` levels a side. */
export function slotCount(rowsPerSide: number): number {
  return rowsPerSide * 2 + 1
}

const EMPTY: Slot = { kind: 'empty', cents: null, ghost: false }
const SPREAD: Slot = { kind: 'spread', cents: null, ghost: false }

/**
 * Lay the book out into `2 · rowsPerSide + 1` rows, top to bottom.
 *
 * `asks` and `bids` are price lists **best first** — asks ascending, bids
 * descending — and may be longer than `rowsPerSide`: an anchored layout shows
 * whatever real levels fall in its window, so it is handed the whole side.
 *
 * Unanchored, asks render best-*last* so the spread sits in the middle, a short
 * ask side pads at the top (the blanks belong away from the spread), and a short
 * bid side pads at the bottom.
 */
export function layoutSlots(
  asks: readonly number[],
  bids: readonly number[],
  rowsPerSide: number,
  anchor: Anchor | null,
): Slot[] {
  const total = slotCount(rowsPerSide)
  const column = columnOf(asks, bids, anchor)

  // Where the column starts relative to row 0. Unanchored, the spread sits in
  // the middle row; anchored, the anchored entry sits in its own row.
  const spreadAt = column.findIndex((slot) => slot.kind === 'spread')
  let start = spreadAt - rowsPerSide
  if (anchor) {
    const held = column.findIndex(
      (slot) => slot.kind === anchor.kind && (anchor.kind === 'spread' || slot.cents === anchor.cents),
    )
    if (held >= 0) start = held - clamp(anchor.slot, 0, total - 1)
  }

  const out = new Array<Slot>(total)
  for (let row = 0; row < total; row++) out[row] = column[start + row] ?? EMPTY
  return out
}

/**
 * The anchor for a pointer over row `row` of `slots`.
 *
 * An empty row anchors the **nearest row that is not empty**, at that row's
 * own position: a pointer resting on the padding above a thin book must not
 * have a level slide in under it either. `null` only for a layout with nothing
 * in it at all, which cannot happen — the spread is always a row.
 */
export function anchorAt(slots: readonly Slot[], row: number): Anchor | null {
  for (let distance = 0; distance < slots.length; distance++) {
    for (const at of distance === 0 ? [row] : [row - distance, row + distance]) {
      const slot = slots[at]
      if (!slot || slot.kind === 'empty') continue
      return { kind: slot.kind, cents: slot.cents, slot: at }
    }
  }
  return null
}

// ---------------------------------------------------------------------------

/**
 * The whole book as one column, top to bottom: every ask worst → best, the
 * spread, every bid best → worst. An anchored price that has left its side is
 * put back in its sorted place, flagged as a ghost.
 */
function columnOf(asks: readonly number[], bids: readonly number[], anchor: Anchor | null): Slot[] {
  const askCents = withGhost(asks, anchor?.kind === 'ask' ? anchor.cents : null, 'ascending')
  const bidCents = withGhost(bids, anchor?.kind === 'bid' ? anchor.cents : null, 'descending')

  const column: Slot[] = []
  for (let i = askCents.length - 1; i >= 0; i--) column.push(slotOf('ask', askCents[i] as GhostCents))
  column.push(SPREAD)
  for (const entry of bidCents) column.push(slotOf('bid', entry))
  return column
}

interface GhostCents {
  cents: number
  ghost: boolean
}

/** `levels` (best first) with `ghost` inserted in order if it is missing. */
function withGhost(
  levels: readonly number[],
  ghost: number | null,
  order: 'ascending' | 'descending',
): GhostCents[] {
  const out = levels.map((cents) => ({ cents, ghost: false }))
  if (ghost === null || levels.includes(ghost)) return out
  const after = (cents: number) => (order === 'ascending' ? cents > ghost : cents < ghost)
  const at = out.findIndex((entry) => after(entry.cents))
  out.splice(at < 0 ? out.length : at, 0, { cents: ghost, ghost: true })
  return out
}

function slotOf(kind: 'ask' | 'bid', entry: GhostCents): Slot {
  return { kind, cents: entry.cents, ghost: entry.ghost }
}

function clamp(value: number, low: number, high: number): number {
  return Math.min(high, Math.max(low, value))
}
